import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { trackError } from 'utils/trackError';
import { isStreamableContainer, streamFileAsWebm } from 'utils/audioTranscode';
import { useRecordingSession } from 'providers/RecordingSessionProvider';
import type { ResultData } from 'types/result';

export const UploadStep = {
  IDLE: 'idle',
  PREPARING: 'preparing',
  TRANSCODING: 'transcoding',
  STREAMING: 'streaming',
  WAITING_FOR_RESULT: 'waiting_for_result',
} as const;
export type UploadStep = (typeof UploadStep)[keyof typeof UploadStep];

export interface AudioUploadStartOptions {
  languageCode?: string;
  anamnesisId?: string;
  visitReasonId?: string;
  medatixxId?: string | null;
  fullname?: string | null;
  birthdate?: string | null;
}

interface UseAudioUploadReturn {
  uploadedAudioFile: File | null;
  uploadError: Error | null;
  isUploading: boolean;
  handleAudioFileSelect: (file: File) => void;
  handleUploadFile: (options: AudioUploadStartOptions) => Promise<void>;
  uploadStep: UploadStep;
  clearSelectedFile: () => void;
  cancelUpload: () => void;
}

export const useAudioUpload = (): UseAudioUploadReturn => {
  const navigate = useNavigate();

  const [uploadedAudioFile, setUploadedAudioFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStep, setUploadStep] = useState<UploadStep>(UploadStep.IDLE);
  const cancelledRef = useRef(false);

  const {
    connect: webSocketConnect,
    start: webSocketStart,
    sendChunk: webSocketSendChunk,
    end: webSocketEnd,
    disconnect: webSocketDisconnect,
  } = useRecordingSession();

  const handleAudioFileSelect = useCallback((file: File) => {
    setUploadedAudioFile(file);
    setUploadError(null);
  }, []);

  const clearSelectedFile = useCallback(() => {
    setUploadedAudioFile(null);
    setUploadError(null);
  }, []);

  const cancelUpload = useCallback(() => {
    cancelledRef.current = true;
    webSocketDisconnect();
    setUploadedAudioFile(null);
    setUploadError(null);
    setIsLoading(false);
    setUploadStep(UploadStep.IDLE);
  }, [webSocketDisconnect]);

  const uploadAudioFile = useCallback(
    async (file: File, { languageCode, ...metadata }: AudioUploadStartOptions) => {
      cancelledRef.current = false;
      setIsLoading(true);
      setUploadStep(UploadStep.PREPARING);

      const throwIfCancelled = () => {
        if (cancelledRef.current) throw new Error('Upload cancelled');
      };

      const runStream = async (transcode: boolean) => {
        await webSocketConnect();
        throwIfCancelled();
        await webSocketStart({ language: languageCode, ...metadata });
        throwIfCancelled();
        setUploadStep(transcode ? UploadStep.TRANSCODING : UploadStep.STREAMING);
        if (transcode) {
          await streamFileAsWebm(file, webSocketSendChunk);
        } else {
          await webSocketSendChunk(file);
        }
        throwIfCancelled();
        setUploadStep(UploadStep.WAITING_FOR_RESULT);
        return webSocketEnd();
      };

      try {
        setUploadError(null);
        const result = (await runStream(!isStreamableContainer(file.type))) as
          ResultData | undefined;
        if (result?.id != null) {
          void navigate(`/result/${encodeURIComponent(String(result.id))}`, {
            state: { result, languageCode },
          });
        }
      } catch (error) {
        if (cancelledRef.current) return;
        trackError(error, { context: 'audioUpload' });
        setUploadError(error instanceof Error ? error : new Error('Unknown error'));
        setUploadedAudioFile(null);
        webSocketDisconnect();
      } finally {
        setIsLoading(false);
        setUploadStep(UploadStep.IDLE);
      }
    },
    [
      navigate,
      webSocketConnect,
      webSocketStart,
      webSocketSendChunk,
      webSocketEnd,
      webSocketDisconnect,
    ]
  );

  const handleUploadFile = useCallback(
    async (options: AudioUploadStartOptions) => {
      if (!uploadedAudioFile) return;
      await uploadAudioFile(uploadedAudioFile, options);
    },
    [uploadedAudioFile, uploadAudioFile]
  );

  return {
    uploadedAudioFile,
    uploadError,
    isUploading: isLoading,
    handleAudioFileSelect,
    handleUploadFile,
    uploadStep,
    clearSelectedFile,
    cancelUpload,
  };
};
